import { useState, useRef, useEffect, useCallback } from 'react';
import { fetchSummary, fetchVms } from '../services/esxiApi';
import {
  normalizeSummary,
  reconcileVmAfterOperation,
} from '../utils/format';

/**
 * Classifica un errore API ESXi in una categoria mostrabile all'utente, senza
 * mai esporre output SSH grezzo o dettagli interni del server.
 */
function classifyError(err) {
  const data = err?.response?.data || {};
  const code = data.code || data.error;
  const status = err?.response?.status;
  if (code === 'ESXI_NOT_CONFIGURED' || status === 503) {
    if (code === 'ESXI_UNAVAILABLE') {
      return { kind: 'unreachable', message: 'Host ESXi non raggiungibile.' };
    }
    if (code === 'ESXI_NOT_CONFIGURED') {
      return { kind: 'not_configured', message: 'Host ESXi non configurato.' };
    }
  }
  if (code === 'ESXI_UNAVAILABLE') {
    return { kind: 'unreachable', message: 'Host ESXi non raggiungibile.' };
  }
  return {
    kind: 'error',
    message: data.message || 'Errore durante il caricamento dei dati ESXi.',
  };
}

/**
 * Carica e aggiorna l'inventario ESXi (summary + elenco VM) in un'unica
 * chiamata combinata. Distingue caricamento iniziale e aggiornamento manuale,
 * gestisce stale/host non configurato/host irraggiungibile, e annulla le
 * richieste obsolete allo smontaggio.
 */
export function useEsxiInventory() {
  const [summary, setSummary] = useState(null); // normalizzato
  const [vms, setVms] = useState([]);
  const [catalogWarnings, setCatalogWarnings] = useState(null);
  const [loading, setLoading] = useState(true); // solo primo caricamento
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null); // { kind, message } | null
  const [lastUpdated, setLastUpdated] = useState(null);

  const mountedRef = useRef(true);
  const abortRef = useRef(null);
  const loadedOnceRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const load = useCallback((refresh = false) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (loadedOnceRef.current) setRefreshing(true);
    else setLoading(true);
    setError(null);

    return Promise.all([
      fetchSummary(refresh, controller.signal),
      fetchVms(refresh ? { refresh: true } : {}, controller.signal),
    ])
      .then(([summaryRes, vmsRes]) => {
        if (!mountedRef.current || controller.signal.aborted) return;
        const items = Array.isArray(vmsRes?.items) ? vmsRes.items : [];
        const warnings =
          vmsRes?.catalogWarnings || summaryRes?.catalogWarnings || null;
        setVms(items);
        setCatalogWarnings(warnings);
        setSummary(normalizeSummary(summaryRes, items));
        setLastUpdated(
          summaryRes?.generatedAt || vmsRes?.generatedAt || new Date().toISOString(),
        );
        loadedOnceRef.current = true;
      })
      .catch((err) => {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
        if (!mountedRef.current) return;
        setError(classifyError(err));
      })
      .finally(() => {
        if (!mountedRef.current) return;
        setLoading(false);
        setRefreshing(false);
      });
  }, []);

  // Caricamento iniziale.
  useEffect(() => {
    load(false);
  }, [load]);

  const refresh = useCallback(() => load(true), [load]);

  /**
   * Aggiorna immediatamente la VM con lo stato finale che il backend ha già
   * verificato. Il refresh live parte comunque subito dopo, ma non è più
   * necessario attenderlo per vedere stato e azione coerenti.
   */
  const applyOperationResult = useCallback((catalogId, operation) => {
    setVms((previous) => {
      let changed = false;
      const next = previous.map((vm) => {
        if (vm.catalogId !== catalogId) return vm;
        const reconciled = reconcileVmAfterOperation(vm, operation);
        changed = changed || reconciled !== vm;
        return reconciled;
      });
      return changed ? next : previous;
    });
  }, []);

  return {
    summary,
    vms,
    catalogWarnings,
    loading,
    refreshing,
    error,
    lastUpdated,
    stale: Boolean(summary?.stale),
    refresh,
    applyOperationResult,
  };
}
